import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ROUTE_TABLE_BASE } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-route-master',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VioTablesV2Module],
  templateUrl: './route-master.component.html',
  styleUrl: './route-master.component.scss',
})
export class RouteMasterComponent implements OnInit {
  view: 'list' | 'form' = 'list';

  records: any[] = [];
  runners: any[] = [];
  isListLoading = false;

  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  showDeleteConfirm: string | null = null;
  isDeleting = false;

  tableConfig: VT2_TABLE_CONFIG = {
    ...ROUTE_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
    ],
  };

  constructor(private fb: FormBuilder, private api: ApiService, private pageTitle: PageTitleService, private dateFmt: DateFormatService) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Route Master', 'Manage delivery routes and runner assignments');
    this.buildForm();
    this.loadRunners();
    this.loadList();
  }

  buildForm(): void {
    this.form = this.fb.group({
      routeid:     ['', [Validators.required, Validators.maxLength(20)]],
      description: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      dataofrun:   [null, [Validators.required, Validators.min(1), Validators.max(31)]],
      runnerid:    ['', [Validators.required]],
    });
  }

  loadRunners(): void {
    this.api.getRunners().subscribe({
      next: (data) => { this.runners = data || []; },
      error: () => { this.runners = []; },
    });
  }

  getRunnerName(runnerId: string): string {
    const runner = this.runners.find(r => r._id === runnerId);
    return runner ? runner.name : runnerId;
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getRouteMasters().subscribe({
      next: (data) => {
        this.records = data || [];
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.form.reset();
    this.successMessage = '';
    this.errorMessage = '';
    this.showDeleteConfirm = null;
    this.view = 'form';
    this.pageTitle.set('Route Master', 'Add a new route');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id;
    this.successMessage = '';
    this.errorMessage = '';
    this.showDeleteConfirm = null;
    this.form.patchValue({
      routeid:     record.routeid,
      description: record.description,
      dataofrun:   record.dataofrun,
      runnerid:    record.runnerid,
    });
    this.view = 'form';
    this.pageTitle.set('Route Master', 'Update route details');
  }

  backToList(): void {
    this.view = 'list';
    this.pageTitle.set('Route Master', 'Manage delivery routes and runner assignments');
    this.loadList();
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request$ = this.isEditMode
      ? this.api.updateRouteMaster({ ...this.form.value, id: this.editId })
      : this.api.saveRouteMaster(this.form.value);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'Route updated successfully!' : 'Route saved successfully!';
        setTimeout(() => this.backToList(), 1000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.message || 'Failed to save route.';
      },
    });
  }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.errorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
  }

  deleteRecord(id: string): void {
    this.isDeleting = true;
    this.api.deleteRouteMaster(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.errorMessage = err?.message || 'Failed to delete route.';
      },
    });
  }
}
